import './DeepAnalysis.less';
import React from 'react';
import { Card, Statistic, Alert, Row, Col, Button } from 'antd';
import ScoreIndicator from './shared/ScoreIndicator';
import { useNavigate } from '../router/hooks/useNavigate';
import { useFetchServiceableUrlsCountQuery } from './serviceableUrls/service';
import { GenericProcess } from './process/types';
import useBatchUrlAnalysisProcessesPolling from './process/hooks/useBatchUrlAnalysisProcessesPolling';
import SmallSpinner from './shared/SmallSpinner';
import { DPA_DASHBOARD_POLLING_TIME_MS } from './settings';
import { useFetchWebsiteIndexScheduledTasksQuery, useFetchOnPageSeoScoreQuery } from './websiteIndexEntry/service';
import moment from 'moment';
import { WebsiteIndexScheduledTasks } from './websiteIndexEntry/types';

type Props = {
    websiteGuid: string;
};

function getFormattedDate(date: string) {
    return moment.utc(date).local().format('MMM DD, YYYY [at] hh:mm A');
}

function getNextExecutionDate(scheduledTasks: WebsiteIndexScheduledTasks | undefined) {
    if (!scheduledTasks || !scheduledTasks.recurrent_on_page_seo_batch_url_analysis) {
        return null;
    }

    const recurrentBatchUrlAnalysisTasks = scheduledTasks.recurrent_on_page_seo_batch_url_analysis;
    if (recurrentBatchUrlAnalysisTasks.length === 0) {
        return null;
    }
    const lastTask = recurrentBatchUrlAnalysisTasks.slice(-1)[0];

    return lastTask.execute_at;
}

export default function DeepAnalysisDashboardOverview({ websiteGuid }: Props) {
    const { data: serviceableUrlsCount, isLoading: isLoadingServiceableUrlsCount } = useFetchServiceableUrlsCountQuery({ w_guid: websiteGuid });
    const { isRunningBatchAnalysis, activeBatchUrlAnalysisProcess, isLoadingRunningProcesses } = useBatchUrlAnalysisProcessesPolling(websiteGuid);
    const { data, isLoading: isLoadingOnPageSeoScore } = useFetchOnPageSeoScoreQuery(
        { guid: websiteGuid },
        {
            pollingInterval: isRunningBatchAnalysis ? DPA_DASHBOARD_POLLING_TIME_MS : 0,
            refetchOnMountOrArgChange: isRunningBatchAnalysis ? DPA_DASHBOARD_POLLING_TIME_MS / 1000 : false,
        }
    );

    const { data: scheduledTasks } = useFetchWebsiteIndexScheduledTasksQuery({ guid: websiteGuid });
    const nextExecutionDate = getNextExecutionDate(scheduledTasks);

    const getAlertTitle = (process: GenericProcess | null) => {
        if (process && process.subprocesses_count >= 0) {
            const finished = process.completed_subprocesses_count + process.failed_subprocesses_count + process.timeout_subprocesses_count;
            const progress = Math.floor((finished / process.subprocesses_count) * 100);
            return `SEO audit in progress - ${progress}% (${finished}/${process.subprocesses_count})`;
        }
        return 'SEO audit in progress';
    };

    return (
        <>
            {isRunningBatchAnalysis && (
                <Alert
                    className="audit-in-progress-alert"
                    type="info"
                    showIcon
                    message={
                        <div className='analysis-progress'>
                            <div className='title'>{getAlertTitle(activeBatchUrlAnalysisProcess)}</div>
                            <div className="loader"></div>
                        </div>
                    }
                    description={
                        <>
                            We are currently analyzing your monitored URLs for SEO issues. This may take several minutes or more depending on the complexity of
                            your site's pages.
                            <br />
                            Feel free to leave this page - we'll email and notify you when it's complete.
                        </>
                    }
                />
            )}

            <div className="deep-analysis-dashboard-overview">
                <Card className="deep-analysis-dashboard-overview-card">
                    <Row
                        type="flex"
                        justify="space-between"
                    >
                        <Col span={8}>
                            <OverviewContent
                                title="Domain SEO Score"
                                value={data?.overall_score || 0}
                                isSeoScore
                                loading={isLoadingOnPageSeoScore}
                                showDecimals
                            />
                        </Col>

                        {/* <Col span={4}>
                            <OverviewContent
                                title="Website Configuration Score"
                                value={data?.domain_score || 0}
                                isSeoScore
                                loading={isLoadingOnPageSeoScore}
                                redirectTo={`/deep-analysis/report?w_guid=${websiteGuid}`}
                            />
                        </Col>

                        <Col span={4}>
                            <OverviewContent
                                title="Page Groups Avg. SEO Score"
                                value={data?.clusters_avg_score || 0}
                                isSeoScore
                                loading={isLoadingOnPageSeoScore}
                                showDecimals
                            />
                        </Col> */}

                        <Col span={8}>
                            <OverviewContent
                                title="Monitored URLs"
                                value={serviceableUrlsCount?.count || 0}
                                loading={isLoadingServiceableUrlsCount}
                                redirectTo={`/deep-analysis/monitored-urls?w_guid=${websiteGuid}`}
                            />
                        </Col>

                        <Col span={8}>
                            <Statistic
                                title="Analysis Status"
                                prefix={
                                    <div className="analysis-col">
                                        {!isLoadingRunningProcesses && scheduledTasks && <span>{isRunningBatchAnalysis ? 'In progress' : 'Completed'}</span>}
                                        {!isRunningBatchAnalysis && nextExecutionDate && (
                                            <span className="extra">
                                                Next analysis: {getFormattedDate(nextExecutionDate)}
                                            </span>
                                        )}
                                    </div>
                                }
                                formatter={() => null}
                            />
                        </Col>
                    </Row>
                </Card>
            </div>
        </>
    );
}

type OverviewContentProps = {
    title: string;
    loading: boolean;
    value: number | string;
    isSeoScore?: boolean;
    redirectTo?: string;
    showDecimals?: boolean;
};

function OverviewContent({ title, value, isSeoScore, redirectTo, loading, showDecimals }: OverviewContentProps) {
    const navigate = useNavigate();

    return (
        <Statistic
            title={title}
            prefix={
                loading ? (
                    <SmallSpinner />
                ) : (
                    <div className="overview-card-content">
                        {isSeoScore && (
                            <ScoreIndicator
                                size="big"
                                value={value as number}
                                showDecimals={!!showDecimals}
                            />
                        )}
                        {!isSeoScore && <span>{value}</span>}
                        {redirectTo && (
                            <Button
                                className="view-details"
                                size="small"
                                icon="eye"
                                onClick={() => navigate(redirectTo)}
                            />
                        )}
                    </div>
                )
            }
            formatter={() => null}
        />
    );
}
